import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Home, 
  QrCode, 
  Users, 
  Bell, 
  Plus,
  CheckCircle,
  CheckCircle2,
  Smartphone,
  Mail,
  Shield,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Zap,
  Target,
  Clock,
  Play,
  Sparkle,
  Building2,
  BarChart3,
  ClipboardList,
  MessageCircle,
  Wrench,
  CalendarClock,
  ListChecks
} from 'lucide-react';
import MobileNav from '../components/MobileNav';
import SupportChat from '../components/SupportChat';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { getUser } from '../lib/auth';

const adminSteps = [
  {
    id: 1,
    title: "Create Your Properties",
    icon: Home,
    color: "from-blue-500 to-blue-600",
    description: "Add your flats, rooms, or rental properties to get started.",
    details: [
      "Go to the Properties page",
      "Click '+ New Property' button",
      "Enter property name (e.g., 'Flat 1', 'Beach House')",
      "Add the address and bed configuration (optional but recommended)",
      "Each property gets a unique QR code automatically"
    ],
    tip: "Use clear, memorable names for easy identification"
  },
  {
    id: 2,
    title: "Print & Display QR Codes",
    icon: QrCode,
    color: "from-emerald-500 to-emerald-600",
    description: "Download and place QR codes in your properties for guest checkout.",
    details: [
      "Click 'View QR' on any property card",
      "Download or print the QR code",
      "Place it where guests will see it: by the exit door, in the guest guide book, or both",
      "You can also copy the checkout link and send it to guests by message",
      "Guests scan the code (or open the link) when they check out",
      "You and any assigned staff get an instant notification"
    ],
    tip: "Put the QR in the guest guide book, laminate one by the door, or both. If you prefer, send guests the checkout link by message in your checkout instructions. Just make sure they know where to find it."
  },
  {
    id: 3,
    title: "Build Your Team",
    icon: Users,
    color: "from-purple-500 to-purple-600",
    description: "Invite staff members to help manage properties and receive notifications.",
    details: [
      "Go to the Team page",
      "Click 'Invite' button",
      "Enter staff member's email",
      "They receive an invitation link",
      "Once accepted, assign properties to them"
    ],
    tip: "Staff only see properties assigned to them"
  },
  {
    id: 4,
    title: "Assign Properties to Staff",
    icon: Target,
    color: "from-orange-500 to-orange-600",
    description: "Control which properties each team member can access.",
    details: [
      "On the Team page, find the staff member",
      "Click 'Manage Properties' button",
      "Select the properties to assign",
      "Save changes",
      "The staff member gets notified via email"
    ],
    tip: "Admins always see all properties"
  },
  {
    id: 5,
    title: "Receive Checkout Notifications",
    icon: Bell,
    color: "from-pink-500 to-pink-600",
    description: "Get instant alerts when guests check out and when cleaning is updated.",
    details: [
      "Turn on Push and/or Email in Settings → Notifications",
      "A guest scans the QR code on checkout (or staff taps 'Checked Out')",
      "You get alerted the moment a checkout happens",
      "Enable 'Cleaning updates' to be alerted when staff start cleaning and mark a property ready",
      "See everything on the Home and Checkouts pages"
    ],
    tip: "Push and Email send the same alerts; pick whichever suits you"
  },
  {
    id: 6,
    title: "Track Activity & Cleaning Status",
    icon: Clock,
    color: "from-cyan-500 to-cyan-600",
    description: "Follow every checkout and its cleaning status by date and staff.",
    details: [
      "Open the Home page for today's Daily Activity (Checked out / Cleaning / Ready)",
      "Use the calendar to jump to any date",
      "Filter by a specific staff member to see only their properties",
      "Open the Checkouts page for the full history",
      "Each entry shows who started cleaning and who marked it ready"
    ],
    tip: "Use the staff filter to review one cleaner's turnovers at a glance"
  },
  {
    id: 7,
    title: "Schedule Late Checkouts",
    icon: CalendarClock,
    color: "from-orange-500 to-red-500",
    description: "When a guest asks to leave later, schedule it once and the cleaners are notified automatically.",
    details: [
      "Open the Checkouts tab and tap 'Schedule Late Checkout'",
      "Pick the property, the checkout date and the new checkout time (add a note if useful)",
      "The assigned cleaners get an instant push, so nobody has to message them",
      "Everyone sees the day's late checkouts in the 'Late checkouts' box on Home",
      "Only admins can add, edit or remove them — it's a heads-up and doesn't change cleaning status"
    ],
    tip: "Tap a late checkout on Home to edit its time or note without deleting it"
  },
  {
    id: 8,
    title: "Scheduled Tasks",
    icon: ListChecks,
    color: "from-orange-500 to-amber-500",
    description: "Add recurring or one-off jobs to a property (laundry, bins for the council) that cleaners see on the right day.",
    details: [
      "Open a property's create or edit form and use 'Add task' in the Scheduled tasks section",
      "Choose One-off (single date + time) or Recurring (Every week / 2 weeks / Monthly, with optional end)",
      "Cleaners get a red 'Daily Task' badge on the Home card only on the day it's due",
      "When they mark the flat Ready they must tick each task first — a built-in reminder",
      "Admins and managers manage tasks; they're in-app only and don't change cleaning status"
    ],
    tip: "Give tasks clear names so the team knows exactly what to do"
  }
];

const staffSteps = [
  {
    id: 1,
    title: "See Your Properties",
    icon: Home,
    color: "from-blue-500 to-blue-600",
    description: "You only see the properties your admin has assigned to you.",
    details: [
      "Open the Properties page to see your assigned units",
      "Open the Home page for today's checkouts and cleaning status",
      "You'll only see the properties assigned to you"
    ],
    tip: "If a property is missing, ask your admin to assign it to you"
  },
  {
    id: 2,
    title: "A Guest Checks Out",
    icon: QrCode,
    color: "from-emerald-500 to-emerald-600",
    description: "A checkout appears when the guest scans the QR code, or you can register it yourself.",
    details: [
      "Normally the guest scans the property's QR code when leaving",
      "If they forget, open the Properties page and tap the green 'Checked Out' button on that property",
      "Confirm, and the checkout is registered instantly",
      "The property then shows up in your Home → Daily Activity"
    ],
    tip: "You must register the checkout before you can start cleaning"
  },
  {
    id: 3,
    title: "Start Cleaning",
    icon: Play,
    color: "from-amber-500 to-amber-600",
    description: "Let everyone know a unit is being cleaned.",
    details: [
      "On the Home page, find the checked-out property",
      "Tap the 'Start Cleaning' button",
      "The status changes to 'Cleaning'",
      "Your admin will be notified when the cleaning has begun"
    ],
    tip: "Tap this as soon as you begin so the status stays accurate"
  },
  {
    id: 4,
    title: "Mark Ready",
    icon: CheckCircle2,
    color: "from-emerald-500 to-emerald-600",
    description: "Finish the turnover when the property is guest-ready.",
    details: [
      "Once cleaning is done, tap the 'Mark Ready' button",
      "The status changes to 'Ready'",
      "Your admin is notified the property is ready for the next guest",
      "That's it. Turnover complete!"
    ],
    tip: "Only mark ready when the unit is fully prepared for the next guest"
  },
  {
    id: 5,
    title: "Check Your Day",
    icon: Clock,
    color: "from-cyan-500 to-cyan-600",
    description: "Keep track of what still needs doing.",
    details: [
      "The Home page shows Checked out / Cleaning / Ready counts for the day",
      "Open the Checkouts page to see the full history",
      "Choose which notifications you want: email, push, or both"
    ],
    tip: "Enable Push notifications on your phone to get instant checkout alerts"
  }
];

const adminTips = [
  { icon: Smartphone, title: "Mobile Friendly", description: "Use GuestCheckout from any device: phone, tablet, or computer" },
  { icon: Shield, title: "Role-Based Access", description: "Admins have full control; staff see only assigned properties" },
  { icon: Zap, title: "Instant Alerts", description: "Push & email deliver checkout and cleaning alerts in real-time" },
  { icon: Mail, title: "Email Updates", description: "Team invites and property assignments sent via email" }
];

const staffTips = [
  { icon: Smartphone, title: "On Your Phone", description: "Works great on mobile; install the app for instant push alerts" },
  { icon: Home, title: "Your Properties Only", description: "You see just the units assigned to you, nothing else" },
  { icon: Play, title: "3 Simple Taps", description: "Checked Out → Start Cleaning → Mark Ready" },
  { icon: Bell, title: "Stay Notified", description: "Choose email, push, or both in Settings to catch every checkout" }
];

const moreTopics = [
  {
    id: 'checkout-instructions',
    title: 'Checkout instructions for guests',
    icon: ClipboardList,
    color: 'from-emerald-500 to-teal-500',
    body: "When you create or edit a property, pick the steps you want guests to do before they leave, like take out the rubbish or lock the door. Put them in the order you want and add a note to any step if you need to. Guests see this list on the checkout page after they scan the QR."
  },
  {
    id: 'checkouts-page',
    title: 'Reading the Checkouts page',
    icon: Clock,
    color: 'from-cyan-500 to-blue-500',
    body: "Every checkout shows three times: checked out in blue, cleaning started in amber, and ready in green. Two numbers sum it up. Cleaning is how long the clean took. Turnover is the time from checkout to ready. Use the staff filter to focus on one person or one cleaning company, and use Export to download a date range as a CSV."
  },
  {
    id: 'maintenance',
    title: 'Maintenance reports',
    icon: Wrench,
    color: 'from-amber-500 to-orange-500',
    body: "Flag anything that needs fixing, straight from a checkout card. Available on the Professional plan and above.",
    bullets: [
      "Tap the Maintenance wrench on a Home checkout card, describe the issue, add a photo, and mark it urgent if needed.",
      "The wrench shows the status at a glance: amber for open, red for urgent, green once resolved.",
      "Admins and managers tap Maintenance on the Properties page to see everything in two tabs: To do and Done.",
      "Close an item with a note on what was fixed, and set a reminder for a date and time.",
      "Every report alerts admins by push and email; urgent jobs show a red badge on the Properties menu."
    ]
  },
  {
    id: 'cleaning-companies',
    title: 'Cleaning companies',
    icon: Building2,
    color: 'from-purple-500 to-indigo-500',
    body: "If you work with an outside cleaning company, invite them from the Team page (your plan sets how many you can add). You choose which properties to hand over to them. Their manager then adds their own cleaners and assigns them to those properties. Cleaning company people show up in purple across the app, so you can always tell them apart from your own staff."
  },
  {
    id: 'performance',
    title: 'Performance and leaderboard',
    icon: BarChart3,
    color: 'from-blue-500 to-emerald-500',
    body: "Open a team member's card and tap Full Team Performance to see everyone ranked by average cleaning time, fastest first. Tap a person to see their times per property against the target, with a few practical tips. A cleaning company manager has their own Performance page that shows only their own cleaners."
  },
  {
    id: 'next-guests',
    title: 'Next guests on a turnover',
    icon: Users,
    color: 'from-orange-500 to-pink-500',
    body: "On a checkout card on the Home page you can note how many guests arrive next. Set the number with the plus and minus buttons. Cleaners can see it, but only you and a cleaning company manager can change it."
  }
];

export default function GuidePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedStep, setExpandedStep] = useState(1);
  const [chatOpen, setChatOpen] = useState(false);

  const user = getUser();
  const isAdmin = user?.role === 'admin';
  // /guide = full guide (admin + staff), /guide/staff = staff guide
  const isStaffGuide = location.pathname.includes('/staff');

  const steps = isStaffGuide ? staffSteps : adminSteps;
  const tips = isStaffGuide ? staffTips : adminTips;
  const heroTitle = isStaffGuide ? "Staff Guide" : "How to Use";
  const heroSubtitle = isStaffGuide
    ? "Your quick guide to checkouts & cleaning"
    : "Your complete guide to GuestCheckout";
  const heroBody = isStaffGuide
    ? "Everything you need to handle checkouts and cleaning, in a few simple taps. Follow the steps below."
    : "The smart way to manage guest checkouts for hosts, managers, hotels, and cleaning teams. Follow these simple steps to get started.";

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      <MobileNav />

      <main className="flex-1 mobile-container md:!max-w-2xl pb-28 md:pb-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/settings')}
            className="btn-3d h-12 w-12 rounded-xl flex items-center justify-center bg-white border-2 border-slate-200 hover:border-blue-300"
            data-testid="guide-back-button"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-4xl md:text-5xl font-bold gradient-text">{heroTitle}</h1>
            <p className="text-slate-600 font-medium">{heroSubtitle}</p>
          </div>
        </div>

        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-3d p-8 mb-8 text-center relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-emerald-400/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
          
          <div className="relative z-10">
            {isStaffGuide ? (
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center shadow-lg">
                <Sparkle size={40} className="text-white" />
              </div>
            ) : (
              <div className="w-24 h-24 mx-auto mb-5">
                <img
                  src="/logo-new.png"
                  alt="GuestCheckout"
                  className="w-full h-full object-contain drop-shadow-[0_10px_24px_rgba(15,23,42,0.22)]"
                />
              </div>
            )}
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              {isStaffGuide ? `Welcome${user?.name ? `, ${user.name.split(' ')[0]}` : ''}!` : 'Welcome to GuestCheckout!'}
            </h2>
            <p className="text-slate-600 max-w-lg mx-auto">{heroBody}</p>
          </div>
        </motion.div>

        {/* Quick Tips */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {tips.map((tip, index) => (
            <motion.div
              key={tip.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card-3d p-4 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                <tip.icon size={20} className="text-slate-600" />
              </div>
              <h4 className="font-semibold text-slate-900 text-sm mb-1">{tip.title}</h4>
              <p className="text-xs text-slate-500">{tip.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Ask the assistant */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => setChatOpen(true)}
          className="w-full mb-8 text-left rounded-2xl p-5 flex items-center gap-4 text-white shadow-lg shadow-emerald-500/20"
          style={{ background: 'linear-gradient(135deg, #2563EB 0%, #059669 100%)' }}
          data-testid="guide-ask-assistant"
        >
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <MessageCircle size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-[15px]">Have a question about something specific?</h4>
            <p className="text-white/85 text-sm">Ask the assistant. It knows every part of the app and answers right here.</p>
          </div>
          <ChevronRight size={20} className="flex-shrink-0" />
        </motion.button>

        {/* Step by Step Guide */}
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
              <CheckCircle size={18} className="text-white" />
            </div>
            {isStaffGuide ? 'Your Daily Workflow' : 'Step-by-Step Setup'}
          </h3>

          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card-3d overflow-hidden"
              data-testid={`guide-step-${step.id}`}
            >
              {/* Step Header */}
              <button
                onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
                className="w-full p-5 flex items-center gap-4 text-left hover:bg-slate-50/50 transition-colors"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                  <step.icon size={28} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-400">STEP {step.id}</span>
                  </div>
                  <h4 className="text-lg font-bold text-slate-900">{step.title}</h4>
                  <p className="text-sm text-slate-500 truncate">{step.description}</p>
                </div>
                <motion.div
                  animate={{ rotate: expandedStep === step.id ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={24} className="text-slate-400" />
                </motion.div>
              </button>

              {/* Step Details */}
              <AnimatePresence>
                {expandedStep === step.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                      <div className="bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-2xl p-5">
                        <ul className="space-y-3">
                          {step.details.map((detail, i) => (
                            <motion.li
                              key={i}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1 }}
                              className="flex items-start gap-3"
                            >
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-white text-xs font-bold">{i + 1}</span>
                              </div>
                              <span className="text-slate-700">{detail}</span>
                            </motion.li>
                          ))}
                        </ul>
                        
                        {/* Pro Tip */}
                        <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                              <Zap size={18} className="text-amber-600" />
                            </div>
                            <div>
                              <h5 className="font-semibold text-amber-800 text-sm mb-1">Pro Tip</h5>
                              <p className="text-amber-700 text-sm">{step.tip}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* More features */}
        {!isStaffGuide && (
          <div className="mt-8">
            <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
                <Sparkles size={18} className="text-white" />
              </div>
              More features
            </h3>
            <div className="card-3d p-2 sm:p-4">
              <Accordion type="single" collapsible className="w-full">
                {moreTopics.map((t) => (
                  <AccordionItem key={t.id} value={t.id} data-testid={`guide-topic-${t.id}`}>
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="flex items-center gap-3">
                        <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${t.color} flex items-center justify-center flex-shrink-0`}>
                          <t.icon size={18} className="text-white" />
                        </span>
                        <span className="font-bold text-slate-900">{t.title}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-12 pr-2 pb-2">
                        {t.body && <p className="text-slate-600 text-sm leading-relaxed">{t.body}</p>}
                        {t.bullets && (
                          <ul className="mt-2.5 space-y-2">
                            {t.bullets.map((b, i) => (
                              <li key={i} className="flex items-start gap-2.5 text-slate-600 text-sm leading-relaxed">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 rounded-3xl p-8 relative overflow-hidden shadow-xl shadow-emerald-500/20"
          style={{ background: 'linear-gradient(135deg, #2563EB 0%, #0EA5E9 45%, #059669 100%)' }}
          data-testid="guide-bottom-cta"
        >
          {/* Decorative glow orbs */}
          <div className="absolute -top-10 -right-8 w-40 h-40 bg-white/15 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-12 -left-10 w-44 h-44 bg-emerald-300/25 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

          <div className="relative z-10 flex flex-col items-center text-center">
            {/* Eyebrow pill */}
            <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white text-xs font-bold uppercase tracking-widest">
              <Sparkles size={13} />
              Start Now
            </span>

            {/* Icon badge */}
            <div className="w-16 h-16 mb-4 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg">
              {isStaffGuide ? <Home size={30} className="text-white" /> : <Zap size={30} className="text-white" />}
            </div>

            {isStaffGuide ? (
              <>
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">You're all set!</h3>
                <p className="text-white/85 max-w-sm mb-6">Head to your Home page to see today's checkouts and cleaning tasks.</p>
                <button
                  onClick={() => navigate('/')}
                  className="group h-14 px-8 rounded-2xl bg-white text-blue-600 font-bold text-base flex items-center gap-2.5 mx-auto shadow-lg shadow-blue-900/20 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                  data-testid="guide-cta-home"
                >
                  <Home size={20} />
                  Go to Home
                  <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
                </button>
              </>
            ) : (
              <>
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">Ready to get started?</h3>
                <p className="text-white/85 max-w-sm mb-6">Add your first property and start receiving checkout notifications in seconds.</p>
                {isAdmin ? (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="group h-14 px-8 rounded-2xl bg-white text-emerald-600 font-bold text-base flex items-center gap-2.5 mx-auto shadow-lg shadow-emerald-900/20 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                    data-testid="guide-cta-create-property"
                  >
                    <Plus size={20} strokeWidth={2.5} />
                    Create Property
                    <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/')}
                    className="group h-14 px-8 rounded-2xl bg-white text-blue-600 font-bold text-base flex items-center gap-2.5 mx-auto shadow-lg shadow-blue-900/20 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                    data-testid="guide-cta-home"
                  >
                    <Home size={20} />
                    Go to Home
                    <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </button>
                )}
              </>
            )}
          </div>
        </motion.div>
      </main>
      <SupportChat open={chatOpen} onOpenChange={setChatOpen} />
    </div>
  );
}
